/**
 * Amazon Ads SB fetchMetrics orchestration (Story 19.15, Task 6)
 *
 * The adapter's `fetchMetrics` dispatch routes SB-only requests to
 * `runSbFetchMetrics`. The product-specific bits live here; the heavy
 * pipeline (submit / poll / download / parse) is in shared/report-orchestration.
 *
 * Per Story 19.15 AC #4 / #8 / #9 / #10:
 *   - 24 priority SB metrics across sbCampaigns / sbAdGroup / sbPurchasedProduct
 *   - 90-day max date range per chunk (3× SP)
 *   - 3h reporting delay → `dataFreshnessLagHours: 3`
 *   - 72h cache freshness window (vs SP's 48h)
 *   - 9-row in-flight safeguard via shared pipeline
 */

import {
  adapterErr,
  adapterOk,
  type AdapterContext,
  type AdapterMetrics,
  type AdapterResult,
  type OAuthCredentials,
} from "../adapter";
import {
  buildAmazonAdsCacheKey,
  buildAmazonAdsReportRequest,
  computeAmazonAdsCacheTtl,
  computeRequestHash,
  detectColumnDrift,
  normalizeRequestForHash,
  validateCachePayload,
  type SpReportRecord,
} from "./reports";
import {
  applySbDerivedMetrics,
  buildSbColumnList,
  pickSbReportType,
  type SbReportTypeId,
} from "./sb-column-map";
import {
  runAmazonAdsReportPipeline,
  type AmazonAdsKvApi,
  type AmazonAdsReportJobsApi,
} from "./shared/report-orchestration";
import {
  chunkAndAggregate,
  type ChunkResult,
} from "../async-chunking";
import {
  AMAZON_ADS_REGIONAL_HOSTS,
  type AmazonAdsRegion,
} from "./oauth";

/** Story 19.15 AC #3 — SB supports up to 90 days per report (vs SP's 31). */
export const AMAZON_ADS_SB_MAX_DAYS_PER_REPORT = 90;

/** Story 19.15 AC #8 — SB has a 3h reporting delay. */
export const AMAZON_ADS_SB_DATA_FRESHNESS_LAG_HOURS = 3;

/** Story 19.15 AC #2 — wall-clock budget (carry-forward 19.14). */
export const AMAZON_ADS_SB_REPORT_TIMEOUT_MS = 30_000;

export interface RunSbFetchMetricsInput {
  readonly ctx: AdapterContext;
  readonly credentials: OAuthCredentials;
  readonly dateRange: { start: Date; end: Date };
  readonly integration: {
    readonly accountId: string;
    readonly region: AmazonAdsRegion;
  };
  readonly lwaClientId: string;
  readonly metrics?: ReadonlyArray<string>;
  readonly dimensions?: ReadonlyArray<string>;
  readonly jobs: AmazonAdsReportJobsApi;
  readonly kv: AmazonAdsKvApi;
  readonly fetchImpl?: typeof fetch;
  readonly now?: () => number;
  readonly sleep?: (ms: number) => Promise<void>;
  readonly correlationId?: string;
  readonly newJobId?: () => string;
}

const DEFAULT_SB_METRIC_KEYS = [
  "impressions",
  "clicks",
  "cost",
  "viewableImpressions",
  "sales14d",
  "orders14d",
  "unitsSold14d",
  "newToBrandSales14d",
  "newToBrandPurchases14d",
  "brandedSearches",
] as const;

export async function runSbFetchMetrics(
  input: RunSbFetchMetricsInput
): Promise<AdapterResult<AdapterMetrics>> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const now = input.now ?? Date.now;
  const sleep =
    input.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const correlationId = input.correlationId ?? crypto.randomUUID();
  const newJobId = input.newJobId ?? (() => crypto.randomUUID());

  // Step 1 — request validation.
  const validation = validateRequest(input);
  if (!validation.ok) return adapterErr(validation.error);

  // Step 2 — region.
  if (!AMAZON_ADS_REGIONAL_HOSTS[input.integration.region]) {
    return adapterErr({
      code: "invalid_response",
      message: `Unknown Amazon Ads region: ${input.integration.region}`,
      retryable: false,
    });
  }

  // Step 3 — pick report type.
  const dimensions = input.dimensions ?? [];
  const reportType = pickSbReportType(dimensions);
  if (!reportType.ok) {
    return adapterErr({
      code:
        reportType.error === "ad_product_dimension_mismatch"
          ? "invalid_request"
          : "invalid_response",
      message: reportType.message,
      retryable: false,
    });
  }

  // Step 6 — chunking with 90-day chunks.
  const totalDays = daysInclusive(input.dateRange.start, input.dateRange.end);
  const allRecords: SpReportRecord[] = [];
  const allWarnings: string[] = [];

  if (totalDays > AMAZON_ADS_SB_MAX_DAYS_PER_REPORT) {
    const chunkResult = await chunkAndAggregate<SpReportRecord>(
      { startDate: input.dateRange.start, endDate: input.dateRange.end },
      {
        maxDaysPerChunk: AMAZON_ADS_SB_MAX_DAYS_PER_REPORT,
        timeBudgetMs: AMAZON_ADS_SB_REPORT_TIMEOUT_MS,
      },
      (chunk) =>
        runOneSbChunk({
          input,
          fetchImpl,
          now,
          sleep,
          correlationId,
          newJobId,
          reportTypeId: reportType.reportTypeId,
          chunk,
        }),
      now
    );

    if (!chunkResult.success) {
      return adapterErr({
        code: (chunkResult.errorCode as never) ?? "invalid_response",
        message: chunkResult.errorMessage,
        retryable: false,
      });
    }
    if (chunkResult.partial) {
      allWarnings.push(
        `report_chunks_pending: ${chunkResult.chunksCompleted}/${chunkResult.chunksTotal} chunks completed`
      );
    }
    for (const r of chunkResult.records) allRecords.push(r);
    for (const w of chunkResult.warnings) allWarnings.push(w);
  } else {
    const r = await runOneSbChunk({
      input,
      fetchImpl,
      now,
      sleep,
      correlationId,
      newJobId,
      reportTypeId: reportType.reportTypeId,
      chunk: input.dateRange,
    });
    if (r.kind === "error" && r.fatal) {
      return adapterErr({
        code: (r.code as never) ?? "invalid_response",
        message: r.message,
        retryable: false,
      });
    }
    if (r.kind === "ok") {
      for (const rec of r.records) allRecords.push(rec);
      if (r.warnings) for (const w of r.warnings) allWarnings.push(w);
    } else {
      allWarnings.push(`single-chunk non-fatal: ${r.message}`);
    }
  }

  // Step 13 — SB-specific derived metrics (clickThroughRate; viewableRate is
  // emitted directly by Amazon per Round 2 Pre-mortem E).
  const finalRecords = applySbDerivedMetrics(allRecords);

  // Step 16 — return AdapterMetrics with dataFreshnessLagHours: 3.
  return adapterOk({
    platform: "amazon-ads",
    accountId: input.integration.accountId,
    dateRange: input.dateRange,
    records: finalRecords,
    fetchedAt: new Date(now()),
    recordCount: finalRecords.length,
    warnings: allWarnings.map((reason) => ({
      reportType: reportType.reportTypeId,
      reason,
    })),
    metadata: {
      dataFreshnessLagHours: AMAZON_ADS_SB_DATA_FRESHNESS_LAG_HOURS,
      correlationId,
      adProduct: "SPONSORED_BRANDS",
    },
  });
}

interface RunOneSbChunkArgs {
  readonly input: RunSbFetchMetricsInput;
  readonly fetchImpl: typeof fetch;
  readonly now: () => number;
  readonly sleep: (ms: number) => Promise<void>;
  readonly correlationId: string;
  readonly newJobId: () => string;
  readonly reportTypeId: SbReportTypeId;
  readonly chunk:
    | { start: Date; end: Date }
    | { startDate: Date; endDate: Date };
}

async function runOneSbChunk(
  args: RunOneSbChunkArgs
): Promise<ChunkResult<SpReportRecord>> {
  const start = "start" in args.chunk ? args.chunk.start : args.chunk.startDate;
  const end = "end" in args.chunk ? args.chunk.end : args.chunk.endDate;
  const metrics = args.input.metrics ?? DEFAULT_SB_METRIC_KEYS;
  const dimensions = args.input.dimensions ?? [];

  // Step 4 — build request body + hash.
  const built = buildAmazonAdsReportRequest({
    adProduct: "SPONSORED_BRANDS",
    reportTypeId: args.reportTypeId,
    startDate: start,
    endDate: end,
    metrics,
    dimensions,
  });
  const normalized = normalizeRequestForHash(built.body);
  const requestHash = await computeRequestHash(normalized);

  // Re-build with hash short suffix for grep-able report name.
  const namedBuilt = buildAmazonAdsReportRequest({
    adProduct: "SPONSORED_BRANDS",
    reportTypeId: args.reportTypeId,
    startDate: start,
    endDate: end,
    metrics,
    dimensions,
    requestHashShort: requestHash.slice(0, 8),
  });

  // Step 5 — KV cache lookup with SB-specific cache key (slug `sb`).
  const cacheKey = buildAmazonAdsCacheKey({
    adProduct: "SPONSORED_BRANDS",
    tenantId: args.input.ctx.tenantId,
    integrationId: args.input.ctx.integrationId,
    requestHash,
  });

  try {
    const cached = await args.input.kv.get(cacheKey);
    if (cached) {
      const parsed = safeJson(cached);
      if (parsed && Array.isArray(parsed.records)) {
        return {
          kind: "ok",
          records: parsed.records as SpReportRecord[],
          warnings: ["cache_hit", ...built.warnings],
        };
      }
    }
  } catch (err) {
    logEvent("amazon_ads_cache_read_failed", {
      correlationId: args.correlationId,
      adProduct: "SPONSORED_BRANDS",
      message: err instanceof Error ? err.message : String(err),
    });
  }

  // Step 7-12 — submit / poll / download / parse via shared pipeline.
  const result = await runAmazonAdsReportPipeline<SpReportRecord>({
    accessToken: args.input.credentials.accessToken,
    clientId: args.input.lwaClientId,
    profileId: args.input.integration.accountId,
    region: args.input.integration.region,
    tenantId: args.input.ctx.tenantId,
    integrationId: args.input.ctx.integrationId,
    body: namedBuilt.body,
    requestHash,
    correlationId: args.correlationId,
    adProduct: "SPONSORED_BRANDS",
    reportTypeId: args.reportTypeId,
    jobs: args.input.jobs,
    parseRecord: (r) => r,
    fetchImpl: args.fetchImpl,
    now: args.now,
    sleep: args.sleep,
    newJobId: args.newJobId,
  });

  if (result.kind === "error") {
    return {
      kind: "error",
      message: result.message,
      code: result.code,
      fatal: !result.retryable,
    };
  }
  if (result.kind === "pending") {
    return {
      kind: "error",
      message: result.message,
      fatal: false,
    };
  }

  const records = result.records;
  const warnings: string[] = [...built.warnings, ...result.warnings];

  // Step 12 — column-drift detection.
  const requestedColumns = buildSbColumnList(
    args.reportTypeId,
    metrics,
    dimensions
  ).columns;
  const drift = detectColumnDrift(requestedColumns, records);
  if (drift.missingColumns.length > 0) {
    logEvent("amazon_ads_column_drift_detected", {
      correlationId: args.correlationId,
      adProduct: "SPONSORED_BRANDS",
      reportId: result.reportId,
      missingColumns: drift.missingColumns,
    });
    for (const w of drift.warnings) warnings.push(w);
  }

  // Step 14 — cache write with SB-aware 72h freshness window.
  const cacheValidation = validateCachePayload(records);
  if (cacheValidation.valid) {
    const ttlSec = computeAmazonAdsCacheTtl(
      { startDate: start, endDate: end },
      "SPONSORED_BRANDS",
      new Date(args.now())
    );
    try {
      await args.input.kv.put(cacheKey, JSON.stringify({ records }), ttlSec);
    } catch (err) {
      logEvent("amazon_ads_cache_write_failed", {
        correlationId: args.correlationId,
        adProduct: "SPONSORED_BRANDS",
        reportId: result.reportId,
        message: err instanceof Error ? err.message : String(err),
      });
    }
  } else {
    logEvent("amazon_ads_cache_validation_failed", {
      correlationId: args.correlationId,
      adProduct: "SPONSORED_BRANDS",
      reportId: result.reportId,
      reason: cacheValidation.reason,
    });
  }

  return { kind: "ok", records, warnings };
}

function validateRequest(
  input: RunSbFetchMetricsInput
):
  | { ok: true }
  | {
      ok: false;
      error: { code: "invalid_response"; message: string; retryable: false };
    } {
  if (
    !(input.dateRange.start instanceof Date) ||
    !(input.dateRange.end instanceof Date) ||
    Number.isNaN(input.dateRange.start.getTime()) ||
    Number.isNaN(input.dateRange.end.getTime())
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message: "Amazon Ads SB fetchMetrics: dateRange contains invalid Date",
        retryable: false,
      },
    };
  }
  if (input.dateRange.start.getTime() > input.dateRange.end.getTime()) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message: "Amazon Ads SB fetchMetrics: dateRange.start > dateRange.end",
        retryable: false,
      },
    };
  }
  if (
    !input.integration.accountId ||
    typeof input.integration.accountId !== "string"
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message:
          "Amazon Ads SB fetchMetrics: integration.accountId (profile ID) is required",
        retryable: false,
      },
    };
  }
  if (!input.lwaClientId) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message:
          "Amazon Ads SB fetchMetrics: AMAZON_ADS_CLIENT_ID env binding missing",
        retryable: false,
      },
    };
  }
  if (
    typeof input.ctx.tenantId !== "string" ||
    input.ctx.tenantId.length === 0
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message:
          "Amazon Ads SB fetchMetrics: ctx.tenantId is required (cross-tenant cache isolation, Round 3 Vuln 1)",
        retryable: false,
      },
    };
  }
  if (
    typeof input.ctx.integrationId !== "string" ||
    input.ctx.integrationId.length === 0
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message: "Amazon Ads SB fetchMetrics: ctx.integrationId is required",
        retryable: false,
      },
    };
  }
  return { ok: true };
}

function daysInclusive(start: Date, end: Date): number {
  const startMs = Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate()
  );
  const endMs = Date.UTC(
    end.getUTCFullYear(),
    end.getUTCMonth(),
    end.getUTCDate()
  );
  return Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1;
}

function safeJson(s: string): { records?: unknown } | null {
  try {
    return JSON.parse(s) as { records?: unknown };
  } catch {
    return null;
  }
}

function logEvent(event: string, details: Record<string, unknown>): void {
  console.log(
    JSON.stringify({
      event,
      ...details,
      timestamp: new Date().toISOString(),
    })
  );
}
